import { CSSProperties } from "react";

/**
 * Picture object produced by vite-imagetools `?responsive` directive
 * (format=avif;webp;jpg + w=480;800;1200;1600 + as=picture).
 */
export type PictureSource = {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
};

interface ResponsiveImageProps {
  /** Either a vite-imagetools picture object OR a plain string src (legacy). */
  source: PictureSource | string;
  alt: string;
  className?: string;
  style?: CSSProperties;
  /** Sizes attribute — defaults to a sensible mobile-first responsive value. */
  sizes?: string;
  loading?: "lazy" | "eager";
  fetchPriority?: "high" | "low" | "auto";
  decoding?: "async" | "sync" | "auto";
}

const ResponsiveImage = ({
  source,
  alt,
  className,
  style,
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 800px",
  loading = "lazy",
  fetchPriority = "auto",
  decoding = "async",
}: ResponsiveImageProps) => {
  // Legacy plain string fallback
  if (typeof source === "string") {
    return (
      <img
        src={source}
        alt={alt}
        className={className}
        style={style}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        sizes={sizes}
      />
    );
  }

  return (
    <picture>
      {Object.entries(source.sources).map(([format, srcset]) => (
        <source
          key={format}
          type={`image/${format}`}
          srcSet={srcset}
          sizes={sizes}
        />
      ))}
      <img
        src={source.img.src}
        width={source.img.w}
        height={source.img.h}
        alt={alt}
        className={className}
        style={style}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        sizes={sizes}
      />
    </picture>
  );
};

export default ResponsiveImage;
